"use client";
import { useRef, useEffect, useState, memo } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { type Resume } from "lib/redux/types";
import { ResumeIframeCSR } from "components/Resume/ResumeIFrame";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { setResume } from "lib/redux/resumeSlice";
import { type Settings, setSettings } from "lib/redux/settingsSlice";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { cx } from "lib/cx";
import { useTemplateComponents } from "components/Resume/templateUtils";
import { useTailwindBreakpoints } from "lib/hooks/useTailwindBreakpoints";
import { type ResumeStats } from "./Dashboard";
import { getDuration, getTimeAgo } from "lib/utils/resumeStats";
import { getFunkyName } from "lib/utils/getFunkyName";
import { usePDF } from "@react-pdf/renderer";
import {
  useRegisterSpecificReactPDFFont,
  useRegisterReactPDFHyphenationCallback,
} from "components/fonts/hooks";
import CustomIcon from "components/CustomIcon";

interface DashboardResumeProps {
  resume: Resume;
  isResumeSelected: boolean;
  handleResumeSelect: (resumeId: number) => void;
  handleDeleteResume: (resumeId: number) => void;
  handleDuplicateResume: (resumeId: number) => void;
  handleResumeShare: () => void;
  handleChangeResumeName: (resumeId: number, resumeName: string) => void;
  setSelectedResume: (resume: Resume) => void;
  resumeSettings: Settings;
  resumeStats?: ResumeStats;
}

interface PopupProps {
  resume: Resume;
  resumeSettings: Settings;
  handleDeleteResume: (resumeId: number) => void;
  handleDuplicateResume: (resumeId: number) => void;
  closePopup: () => void;
  handleEditResume: () =>void
}
const Popup = ({
  resume,
  resumeSettings,
  handleDeleteResume,
  handleDuplicateResume,
  closePopup,
  handleEditResume
}: PopupProps) => {
  const { templateNameToDocument } = useTemplateComponents(
    resume,
    resumeSettings
  );
  useRegisterSpecificReactPDFFont(resumeSettings.fontFamily);
  useRegisterReactPDFHyphenationCallback(resumeSettings.fontFamily);
  const document = templateNameToDocument[resume?.templateId!];
  const [instance] = usePDF({ document });
  const fileName = resume?.profile.name + " - Resume";
  return (
    <div className="absolute right-[43%] top-[68%] z-10 flex h-[150px] w-[105px] flex-col items-start justify-center gap-2 rounded-[21px] bg-[#2C2C2C] px-3 py-2 font-montserrat text-xs text-white md:h-[107px] md:w-[130px] md:px-4 md:py-3 md:text-sm lg:right-0 lg:top-[80%] lg:h-[155px] lg:w-[145px] lg:gap-2 lg:text-base">
      
      <div
        className="active:blue-gradient flex w-full cursor-pointer items-center gap-1 rounded-[10px] p-1 hover:border-[1px] hover:border-white active:border-none md:gap-2"
        onClick={() => {
          handleEditResume();
          closePopup();
        }}
      >
        <CustomIcon iconName="edit" className="h-auto w-3 md:w-5 lg:w-6" /> Edit
      </div>
      <div
        className="active:blue-gradient flex w-full cursor-pointer items-center gap-1 rounded-[10px] p-1 hover:border-[1px] hover:border-white active:border-none md:gap-2"
        onClick={() => {
          handleDuplicateResume(resume.id!);
          closePopup();
        }}
      >
        <DocumentDuplicateIcon className="h-auto w-3 md:w-5 lg:w-6" /> Duplicate
      </div>
      <div
        className="active:blue-gradient flex w-full cursor-pointer items-center gap-1 rounded-[10px] p-1 hover:border-[1px] hover:border-white active:border-none md:gap-2"
        onClick={() => {
          handleDeleteResume(resume.id!);
          closePopup();
        }}
      >
        <TrashIcon className="h-auto w-3 md:w-5 lg:w-5" /> Delete
      </div>
      <a
        className="active:blue-gradient flex w-full cursor-pointer items-center gap-1 rounded-[10px] p-1 hover:border-[1px] hover:border-white active:border-none md:gap-2"
        href={instance.url!}
        download={fileName}
      >
        <ArrowDownTrayIcon className="h-auto w-3 md:w-5 lg:w-6" /> Download
      </a>
    </div>
  );
};

const DashboardResume: React.FC<DashboardResumeProps> = ({
  resume,
  resumeStats,
  isResumeSelected,
  handleResumeSelect,
  handleDeleteResume,
  handleDuplicateResume,
  handleResumeShare,
  handleChangeResumeName,
  setSelectedResume,
  resumeSettings,
}) => {
  const resumeNameRef = useRef<HTMLInputElement>(null);
  const resumeTitleRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const popupRef = useRef<HTMLDivElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [editResumeTitle, setEditResumeTitle] = useState(false);

  const { TemplateNameToComponent, templateNameToDocument } =
    useTemplateComponents(resume, resumeSettings);

  // Use custom hook to detect breakpoints
  const { isMd, isLg, isXl, is2xl } = useTailwindBreakpoints();

  const scale = (() => {
    if (is2xl) return 0.29; // Large screens
    if (isXl) return 0.24;
    if (isLg) return 0.17;
    if (isMd) return 0.4;
    return 0.19; // Default for smaller screens
  })();

  // useEffect(() => {
  //   if (textRef.current && resumeTitleRef.current) {
  //     const newWidth = textRef.current.offsetWidth;
  //     resumeTitleRef.current.style.width = `${newWidth + 2}px`;
  //   }
  // }, [resumeTitle]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowPopup(false); // Close the popup if the click is outside
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  const makeHeadingEditable = () => {
    if (resumeTitleRef.current) {
      resumeTitleRef.current.focus();
    }
  };

  const submitResumeName = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resumeNameRef.current?.value) {
      alert("Resume name cannot be empty");
      return;
    }
    handleChangeResumeName(resume.id!, resumeNameRef.current!.value);
    setEditResumeTitle(false);
  };

  const handleEditResume = () => {
    dispatch(setResume(resume));
    dispatch(setSettings({ ...resumeSettings, template: resume.templateId! }));
    router.push("/resume-builder");
  };

  const handleCheckScore = () => {
    dispatch(setResume(resume));
    dispatch(setSettings({ ...resumeSettings, template: resume.templateId! }));
    router.push("/score-check?source=dashboard");
  };

  const handleResumeSharing = () => {
    setSelectedResume(resume);
    handleResumeShare();
  };

  return (
    <div className="relative flex">
      {/* {Resume Preview} */}
      <div className="h-[54vw] w-[52vw] rounded-2xl bg-white p-2 md:rounded-[37px] md:p-4 lg:h-[20vw] lg:w-[17vw] lg:rounded-2xl lg:p-2">
        <div
          className={cx(
            "relative h-[50%] cursor-pointer overflow-hidden rounded-[10px] border-[12px] border-b-0 md:rounded-[22.76px] md:border-[24px] md:border-b-0 lg:h-[65%] lg:rounded-[10px] lg:border-[12px] lg:border-b-0",
            isResumeSelected
              ? "border-[#178FFE]"
              : "border-[#178FFE] lg:border-[#D9D9D9]"
          )}
          onClick={() => handleResumeSelect(resume.id!)}
        >
          <div className="mx-auto w-fit">
            <ResumeIframeCSR
              documentSize={resumeSettings.documentSize}
              scale={scale}
              enablePDFViewer={DEBUG_RESUME_PDF_FLAG}
              fontFamily={resumeSettings.fontFamily}
            >
              {resume && TemplateNameToComponent[resume.templateId!]}
            </ResumeIframeCSR>
          </div>
        </div>
        <div className="relative flex h-[25%] items-center justify-between font-montserrat lg:h-[20%]">
          <div className="flex flex-col lg:pt-2">
            <div className="flex items-center gap-2 lg:gap-1">
              <span className="text-xs font-medium md:text-2xl lg:text-base">
                {resume.displayName || "My Resume"}
              </span>
              <PencilSquareIcon
                className="h-auto w-3 cursor-pointer text-gray-600 md:w-5"
                onClick={() => setEditResumeTitle(true)}
              />
            </div>
            <div className="text-[7.11px] text-[#0000004F] md:text-lg lg:-mt-1 lg:text-[9.65px]">
              Last updated {getTimeAgo(resume.modifiedOn!)}
            </div>
          </div>
          <div>
            <EllipsisVerticalIcon
              className="h-auto w-4 cursor-pointer md:w-6"
              onClick={() => setShowPopup(!showPopup)}
            />
          </div>
        </div>
        <div className="mt-[2px] hidden gap-x-2 w-full justify-between text-xs lg:flex xl:text-base">
          {/* <button
            className="btn-gradient w-[33%] justify-center py-[5px] text-center"
            onClick={handleEditResume}
          >
            Edit
          </button> */}
          <button
            className="w-[50%] rounded-3xl bg-white py-[5px] text-center text-[#2C2C2C] shadow-xl"
            onClick={handleResumeSharing}
          >
            Share
          </button>
          <button
          onClick={handleCheckScore}
          className="btn-gradient w-[50%] mt-[2px]l justify-center py-[5px] text-center"
        >
          Score
        </button>
        </div>
        
      </div>

      {/* {Resume Statics} */}
      <div className="relative grow pl-2 pt-2 font-montserrat md:pl-4 md:pt-4 lg:hidden">
        <div className="flex items-center justify-around text-[10px] md:text-2xl">
          <div className="flex flex-col items-center">
            {resumeStats?.totalViews}
            <span className="text-[6px] text-[#9E9E9E] md:text-[14.4px]">
              Views
            </span>
          </div>
          <div className="h-4 w-[1px] bg-[#CBCBCB]"></div>
          <div className="flex flex-col items-center">
            {resumeStats?.totalDownloads}
            <span className="text-[6px] text-[#9E9E9E] md:text-[14.4px]">
              Downloads
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2 pt-2">
          {resumeStats?.topSessions.slice(0, 2).map((session, index) => (
            <div
              className="flex justify-between text-[6.29px] md:text-sm"
              key={session.createdOn}
            >
              <div className="flex flex-col font-medium">
                {getFunkyName(session.deviceId)}
                <span className="text-[#9E9E9E]">
                  Viewed for {getDuration(session.duration)} <br />
                  {session.isDownloaded ? "Downloaded" : ""}
                </span>
              </div>
              <div className="text-wrap flex max-w-[15ch] flex-col font-normal">
                {getTimeAgo(session.createdOn)}
                <span className="text-[#9E9E9E]">
                  {session.location.city +
                    ", " +
                    session.location.state +
                    ", " +
                    session.location.country}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute bottom-0 left-0 w-full pl-2 font-poppins text-[10.48px] font-medium md:pl-4 md:text-[26px]">
          <button
            className="btn-gradient w-full justify-center py-[5px] text-center"
            onClick={handleCheckScore}
          >
            Score
          </button>
          <button
            className="mt-2 w-full rounded-3xl bg-white py-[5px] text-center text-[#2C2C2C] shadow-xl"
            onClick={handleResumeSharing}
          >
            Share
          </button>
        </div>
      </div>
      {showPopup && (
        <div ref={popupRef}>
          <Popup
            handleDeleteResume={handleDeleteResume}
            handleDuplicateResume={handleDuplicateResume}
            resume={resume}
            resumeSettings={resumeSettings}
            closePopup={() => setShowPopup(false)}
            handleEditResume={handleEditResume}
          />
        </div>
      )}
      {editResumeTitle && (
        <div className="absolute flex h-full w-full transform items-center justify-center rounded-2xl bg-white/40 backdrop-blur-md transition-transform">
          <XCircleIcon
            className="absolute right-2 top-2 h-6 w-6 cursor-pointer"
            onClick={() => setEditResumeTitle(false)}
          />

          <form onSubmit={submitResumeName} className="rounded-lg  p-4">
            <div className="mb-2">
              <label
                className="block text-xs text-gray-900"
                htmlFor="textField"
              >
                Enter Resume Name
              </label>
              <input
                ref={resumeNameRef}
                type="text"
                id="textField"
                defaultValue={resume.displayName || "My Resume"}
                className="mt-2 w-full rounded-3xl border border-gray-300 p-2 text-[9px] text-gray-700 focus:outline-none md:text-[10px] lg:text-xs"
                placeholder="Resume Name"
              />
            </div>

            <button
              type="submit"
              className="btn-gradient rounded-3xl px-6 py-2 text-[9px] text-white hover:bg-blue-600 disabled:bg-gray-500 md:text-[10px] lg:text-xs"
            >
              Save
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default memo(DashboardResume);
